import { useState } from 'react';
import Papa from 'papaparse';

export interface Wrestler {
  id: string;
  firstName: string;
  lastName: string;
  weightKg: number;
  age?: number;
  grade?: string;
  gender?: string;
}

export interface CsvImportHookReturn {
  wrestlers: Wrestler[];
  originalWrestlers: Wrestler[];
  rawWeights: number[];
  schoolName: string;
  weightUnit: 'kg' | 'lbs';
  loading: boolean;
  error: string | null;
  success: string | null;
  submitted: boolean;
  submitting: boolean;
  csvData: any[];
  detectedColumns: string[];
  columnMappings: Record<string, string>;
  showColumnMapping: boolean;
  nameFormats: Record<string, string>;
  handleFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handlePasteData: (data: string) => void;
  handleWeightUnitChange: (newUnit: 'kg' | 'lbs') => void;
  handleSubmit: () => void;
  handleReset: () => void;
  setSchoolName: (name: string) => void;
  setSubmitted: (submitted: boolean) => void;
  setSuccess: (success: string | null) => void;
  setSubmitting: (submitting: boolean) => void;
  handleColumnMappingChange: (field: string, column: string) => void;
  handleColumnMappingConfirm: (nameFormats?: Record<string, string>) => void;
  handleColumnMappingCancel: () => void;
}

export const useCsvImport = (
  allSubmittedWrestlers: Array<Wrestler & { schoolName: string }>,
  onWrestlersSubmit: (wrestlers: Wrestler[], schoolName: string) => void
): CsvImportHookReturn => {
  const [wrestlers, setWrestlers] = useState<Wrestler[]>([]);
  const [originalWrestlers, setOriginalWrestlers] = useState<Wrestler[]>([]);
  const [rawWeights, setRawWeights] = useState<number[]>([]);
  const [schoolName, setSchoolName] = useState<string>('');
  const [weightUnit, setWeightUnit] = useState<'kg' | 'lbs'>('kg');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [detectedColumns, setDetectedColumns] = useState<string[]>([]);
  const [columnMappings, setColumnMappings] = useState<Record<string, string>>({});
  const [showColumnMapping, setShowColumnMapping] = useState(false);
  const [nameFormats, setNameFormats] = useState<Record<string, string>>({});

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    const fileName = file.name.replace('.csv', '');
    setSchoolName(fileName);
    setLoading(true);
    setError(null);
    setSuccess(null);

    Papa.parse(file, {
      header: true,
      complete: (results) => {
        try {
          const data = results.data.filter((row: any) => row && Object.keys(row).length > 0);
          
          if (data.length === 0) {
            setError('No valid data found in CSV file');
            setLoading(false);
            return;
          }

          // Store raw CSV data and detected columns
          setCsvData(data);
          const firstRow = data[0] as Record<string, any>;
          const columns = Object.keys(firstRow);
          setDetectedColumns(columns);

          // Auto-detect column mappings
          const autoMappings: Record<string, string> = {};
          
          // Common column name variations
          const firstNameVariations = ['firstName', 'first name', 'firstname', 'first', 'givenname', 'given name'];
          const lastNameVariations = ['lastName', 'last name', 'lastname', 'last', 'surname', 'family name'];
          const weightVariations = ['weight', 'weightKg', 'weight (kg)', 'weight (lbs)', 'weightkg', 'weightlbs'];
          const ageVariations = ['age', 'level', 'grade', 'class', 'year'];
          const gradeVariations = ['grade', 'class', 'year', 'level'];
          const genderVariations = ['gender', 'sex', 'malefemale', 'm/f', 'mf'];

          // Try to auto-map columns
          columns.forEach(column => {
            const lowerColumn = column.toLowerCase();
            
            if (firstNameVariations.includes(lowerColumn)) {
              autoMappings.firstName = column;
            } else if (lastNameVariations.includes(lowerColumn)) {
              autoMappings.lastName = column;
            } else if (weightVariations.includes(lowerColumn)) {
              autoMappings.weight = column;
            } else if (ageVariations.includes(lowerColumn)) {
              autoMappings.age = column;
            } else if (gradeVariations.includes(lowerColumn)) {
              autoMappings.grade = column;
            } else if (genderVariations.includes(lowerColumn)) {
              autoMappings.gender = column;
            }
          });

          setColumnMappings(autoMappings);
          setShowColumnMapping(true);
          setSuccess(`CSV file loaded successfully. Please verify column mappings.`);
        } catch (err) {
          setError('Error parsing CSV file. Please check the format.');
        } finally {
          setLoading(false);
        }
      },
      error: () => {
        setError('Error reading CSV file');
        setLoading(false);
      },
    });
  };

  const handlePasteData = (data: string) => {
    if (!data.trim()) {
      setError('No data provided');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    Papa.parse(data, {
      header: true,
      complete: (results) => {
        try {
          const parsedData = results.data.filter((row: any) => row && Object.keys(row).length > 0);
          
          if (parsedData.length === 0) {
            setError('No valid data found in pasted CSV');
            setLoading(false);
            return;
          }

          // Store raw CSV data and detected columns
          setCsvData(parsedData);
          const firstRow = parsedData[0] as Record<string, any>;
          const columns = Object.keys(firstRow);
          setDetectedColumns(columns);

          // Auto-detect column mappings
          const autoMappings: Record<string, string> = {};
          
          // Common column name variations
          const firstNameVariations = ['firstName', 'first name', 'firstname', 'first', 'givenname', 'given name'];
          const lastNameVariations = ['lastName', 'last name', 'lastname', 'last', 'surname', 'family name'];
          const weightVariations = ['weight', 'weightKg', 'weight (kg)', 'weight (lbs)', 'weightkg', 'weightlbs'];
          const ageVariations = ['age', 'level', 'grade', 'class', 'year'];
          const gradeVariations = ['grade', 'class', 'year', 'level'];
          const genderVariations = ['gender', 'sex', 'malefemale', 'm/f', 'mf'];

          // Try to auto-map columns
          columns.forEach(column => {
            const lowerColumn = column.toLowerCase();
            
            if (firstNameVariations.includes(lowerColumn)) {
              autoMappings.firstName = column;
            } else if (lastNameVariations.includes(lowerColumn)) {
              autoMappings.lastName = column;
            } else if (weightVariations.includes(lowerColumn)) {
              autoMappings.weightKg = column;
            } else if (ageVariations.includes(lowerColumn)) {
              autoMappings.age = column;
            } else if (gradeVariations.includes(lowerColumn)) {
              autoMappings.grade = column;
            } else if (genderVariations.includes(lowerColumn)) {
              autoMappings.gender = column;
            }
          });

          setColumnMappings(autoMappings);
          setShowColumnMapping(true);
          setSuccess(`Pasted data loaded successfully. Please verify column mappings.`);
          setLoading(false);
        } catch (error) {
          setError('Error parsing pasted CSV data');
          setLoading(false);
        }
      },
      error: () => {
        setError('Error parsing pasted CSV data');
        setLoading(false);
      },
    });
  };

  const handleWeightUnitChange = (newUnit: 'kg' | 'lbs') => {
    setWeightUnit(newUnit);
    
    if (originalWrestlers.length > 0) {
      let convertedWrestlers;
      
      if (newUnit === 'kg') {
        // Converting from lbs to kg - use raw weights if available
        if (rawWeights.length > 0) {
          convertedWrestlers = originalWrestlers.map((w, index) => ({
            ...w,
            weightKg: rawWeights[index] || w.weightKg * 2.2
          }));
        } else {
          // Fallback: divide by 2.2
          convertedWrestlers = originalWrestlers.map(w => ({
            ...w,
            weightKg: w.weightKg * 2.2
          }));
        }
      } else {
        // Converting from kg to lbs - multiply by 2.2
        convertedWrestlers = originalWrestlers.map(w => ({
          ...w,
          weightKg: w.weightKg / 2.2
        }));
      }
      
      setOriginalWrestlers(convertedWrestlers);
      setWrestlers(convertedWrestlers);
    }
  };

  const handleSubmit = () => {
    if (!schoolName.trim()) {
      setError('Please enter a school name');
      return;
    }

    if (wrestlers.length === 0) {
      setError('No wrestlers to submit');
      return;
    }

    setSubmitting(true);
    setError(null);

    setTimeout(() => {
      onWrestlersSubmit(wrestlers, schoolName);
      setSubmitted(true);
      setSubmitting(false);
    }, 1500);
  };

  const handleReset = () => {
    setWrestlers([]);
    setOriginalWrestlers([]);
    setRawWeights([]);
    setSchoolName('');
    setWeightUnit('kg');
    setError(null);
    setSuccess(null);
    setSubmitted(false);
    setCsvData([]);
    setDetectedColumns([]);
    setColumnMappings({});
    setShowColumnMapping(false);
  };

  const handleColumnMappingChange = (field: string, column: string) => {
    setColumnMappings(prev => ({ ...prev, [field]: column }));
  };

  const handleColumnMappingConfirm = (formats?: Record<string, string>) => {
    // Update name formats if provided
    if (formats) {
      setNameFormats(formats);
    }

    // Helper function to parse names based on format
    const parseNameByFormat = (fullName: string, format: string, fieldKey: string): string => {
      if (!format || format === 'all') return fullName;
      
      const trimmedName = fullName.trim();
      
      // Handle comma-separated names (Last, First)
      if (trimmedName.includes(',')) {
        const parts = trimmedName.split(',').map(part => part.trim());
        if (parts.length >= 2) {
          const lastName = parts[0];
          const firstName = parts[1];
          
          if (format === 'firstName') return fieldKey === 'firstName' ? firstName : '';
          if (format === 'lastName') return fieldKey === 'lastName' ? lastName : '';
          if (format === 'firstNameComma') return fieldKey === 'firstName' ? firstName : '';
          if (format === 'lastNameComma') return fieldKey === 'lastName' ? lastName : '';
        }
      }
      // Handle space-separated names (First Last)
      else {
        const parts = trimmedName.split(/\s+/);
        if (parts.length >= 2) {
          const firstName = parts[0];
          const lastName = parts[parts.length - 1]; // Take last word as last name
          
          if (format === 'firstName') return fieldKey === 'firstName' ? firstName : '';
          if (format === 'lastName') return fieldKey === 'lastName' ? lastName : '';
          if (format === 'firstNameComma') return ''; // No comma present
          if (format === 'lastNameComma') return ''; // No comma present
        }
      }
      
      return fullName; // Fallback
    };

    // Process CSV data using the confirmed mappings
    const processedWrestlers: Wrestler[] = csvData
      .filter((row: any) => row && Object.keys(row).length > 0)
      .map((row: any, index: number) => {
        const weight = parseFloat(row[columnMappings.weight] || '');
        const age = columnMappings.age ? parseInt(row[columnMappings.age]) : undefined;
        const grade = columnMappings.grade ? (isNaN(parseInt(row[columnMappings.grade])) ? row[columnMappings.grade] : parseInt(row[columnMappings.grade])) : undefined;
        const gender = columnMappings.gender ? row[columnMappings.gender] : 'M';
        
        // Apply name formatting
        const rawFirstName = row[columnMappings.firstName] || '';
        const rawLastName = columnMappings.lastName ? (row[columnMappings.lastName] || schoolName.toUpperCase()) : schoolName.toUpperCase();
        
        const firstNameFormat = formats?.firstName || 'all';
        const lastNameFormat = formats?.lastName || 'all';
        
        const firstName = parseNameByFormat(rawFirstName, firstNameFormat, 'firstName');
        const lastName = parseNameByFormat(rawLastName, lastNameFormat, 'lastName');
        
        // Normalize gender to uppercase and ensure it's M or F
        const normalizedGender = gender.toString().toUpperCase().trim();
        const finalGender = normalizedGender.startsWith('M') ? 'M' : 
                           normalizedGender.startsWith('F') ? 'F' : 'M'; // Default to M if unclear

        return {
          id: `wrestler-${Date.now()}-${index}`,
          firstName,
          lastName,
          weightKg: weight || 0,
          age,
          grade,
          gender: finalGender,
        };
      })
      .filter(w => {
        if (!w.firstName || w.firstName.trim() === '') {
          return false;
        }
        
        if (w.weightKg <= 0 || isNaN(w.weightKg)) {
          return false;
        }
        
        return true;
      });

    // Store raw weights for validation
    const weights = csvData
      .filter((row: any) => row && Object.keys(row).length > 0)
      .map((row: any) => parseFloat(row[columnMappings.weight] || ''))
      .filter(weight => weight > 0 && !isNaN(weight));
    
    setRawWeights(weights);
    setOriginalWrestlers(processedWrestlers);
    setWrestlers(processedWrestlers);
    setShowColumnMapping(false);
    setSuccess(`Successfully processed ${processedWrestlers.length} wrestlers from CSV.`);
  };

  const handleColumnMappingCancel = () => {
    setCsvData([]);
    setDetectedColumns([]);
    setColumnMappings({});
    setShowColumnMapping(false);
    setSuccess(null);
  };

  return {
    wrestlers,
    originalWrestlers,
    rawWeights,
    schoolName,
    weightUnit,
    loading,
    error,
    success,
    submitted,
    submitting,
    csvData,
    detectedColumns,
    columnMappings,
    showColumnMapping,
    nameFormats,
    handleFileUpload,
    handlePasteData,
    handleWeightUnitChange,
    handleSubmit,
    handleReset,
    setSchoolName,
    setSubmitted,
    setSuccess,
    setSubmitting,
    handleColumnMappingChange,
    handleColumnMappingConfirm,
    handleColumnMappingCancel,
  };
};
